import { isTimeoutApiError } from '@/api/axiosInstance'
import { projectsApi } from '@/api/projectsApi'
import { type FormField, GenericFormModal } from '@/components/form/GenericFormModal'
import { isRootProjectRecord } from '@/components/organization/organizationUtils'
import { resolveOrganizationId } from '@/lib/routerUtils'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { pollUntil } from '@/utils/pollUntil'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

interface CreateProjectFormValues extends Record<string, unknown> {
	tenantName: string
	serialKey: string
}

const CREATE_PROJECT_FIELDS: FormField[] = [
	{ key: 'tenantName', label: 'Project Name', type: 'text', placeholder: 'Enter project name', required: true },
	{ key: 'serialKey', label: 'Serial Key', type: 'text', placeholder: 'Enter serial key', required: true },
]

const EMPTY_FORM_VALUES: CreateProjectFormValues = {
	tenantName: '',
	serialKey: '',
}

const CREATE_PROJECT_TEST_IDS = {
	root: 'create-project-modal',
	title: 'create-project-title',
	submit: 'create-project-submit',
	cancel: 'create-project-cancel',
	field: {
		tenantName: 'create-project-name',
		serialKey: 'create-project-serial-key',
	},
}

const createProjectSchema = (existingNames: string[]) =>
	z.object({
		tenantName: z
			.string()
			.min(1, 'Please enter a project name.')
			.refine(
				name => !existingNames.some(existing => existing.toLowerCase() === name.trim().toLowerCase()),
				'This project name already exists.',
			),
		serialKey: z
			.string()
			.min(1, 'Serial key cannot be empty')
			.regex(/^\S+$/, 'Serial key cannot contain whitespace')
			.regex(/^[a-zA-Z0-9_-]+$/, 'Serial key can only contain letters, numbers, underscores, and hyphens'),
	})

interface CreateProjectDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSuccess?: (projectId: string) => void
}

export function CreateProjectDialog({ open, onOpenChange, onSuccess }: CreateProjectDialogProps) {
	const [isSubmitting, setIsSubmitting] = useState(false)

	const createProject = useProjectsStore(state => state.createProject)
	const fetchProjects = useProjectsStore(state => state.fetchProjects)
	const allProjects = useProjectsStore(state => state.allProjects)
	const refreshUser = useAuthStore(state => state.refreshUser)
	const user = useAuthStore(state => state.user)

	// Existing project names in this org — enables the form's duplicate-name validation. The
	// organization-level project is left out: it carries the org's own name, and the backend puts no
	// uniqueness constraint on tenant names, so a project may legitimately be named after the org.
	const existingProjectNames = useMemo(() => {
		const organizationId = resolveOrganizationId()
		const names = new Set<string>(
			allProjects.filter(project => !isRootProjectRecord(project)).map(project => project.name),
		)
		if (isPlatformUser(user)) {
			for (const tenant of user.tenant_list ?? []) {
				if (tenant.org_id === organizationId && !isRootProjectRecord(tenant)) {
					names.add(tenant.name || tenant.id)
				}
			}
		}
		return [...names]
	}, [allProjects, user])

	const validationSchema = useMemo(() => createProjectSchema(existingProjectNames), [existingProjectNames])

	const handleCreateProject = useCallback(
		async (values: CreateProjectFormValues) => {
			const tenantName = values.tenantName.trim()
			const serialKey = values.serialKey.trim()

			setIsSubmitting(true)
			try {
				// Serial key travels with the create call, so a project can never exist without one.
				const result = await createProject(tenantName, { serial_key: serialKey })

				// Refresh user data so the new project's serial key is loaded immediately
				await refreshUser()

				toast.success(`Project "${tenantName}" created successfully`)
				// Refresh list in background - don't block success flow
				fetchProjects().catch(err => console.error('Failed to refresh projects:', err))

				onSuccess?.(result.id)
			} catch (error) {
				if (isTimeoutApiError(error)) {
					// The client gave up waiting but the backend keeps provisioning —
					// reconcile against the project list instead of reporting a false failure.
					onOpenChange(false)
					toast.info(`Creating project "${tenantName}" is taking longer than usual — finishing in the background.`)

					const organizationId = resolveOrganizationId()
					// Names are not guaranteed unique — only accept a project that did not
					// exist before this create, so an older same-named project can't be
					// mistaken for the new one.
					const authUser = useAuthStore.getState().user
					const preexistingIds = new Set<string>([
						...useProjectsStore.getState().allProjects.map(project => project.id),
						...(isPlatformUser(authUser) ? (authUser.tenant_list?.map(tenant => tenant.id) ?? []) : []),
					])
					const createdId = await pollUntil(async () => {
						const namesById = await projectsApi.listProjectNames(organizationId)
						for (const [id, name] of namesById) {
							if (name === tenantName && !preexistingIds.has(id)) {
								return id
							}
						}
						return null
					})

					if (!createdId) {
						toast.error(`Could not confirm creation of project "${tenantName}". Check the projects list.`)
						return
					}

					// The original request carried the serial key, so the backend already stored it.
					await refreshUser()
					fetchProjects().catch(err => console.error('Failed to refresh projects:', err))

					toast.success(`Project "${tenantName}" created successfully`)
					onSuccess?.(createdId)
					return
				}

				console.error('Failed to create project:', error)
				const errorMessage = (error as { message?: string })?.message || 'Failed to create project'
				toast.error(errorMessage)
				// Rethrow so the modal stays open on failure rather than discarding what was typed.
				throw error
			} finally {
				setIsSubmitting(false)
			}
		},
		[createProject, fetchProjects, refreshUser, onOpenChange, onSuccess],
	)

	return (
		<GenericFormModal<CreateProjectFormValues>
			isOpen={open}
			onClose={() => onOpenChange(false)}
			mode='create'
			title='Create Project'
			fields={CREATE_PROJECT_FIELDS}
			initialValues={EMPTY_FORM_VALUES}
			onSubmit={handleCreateProject}
			isLoading={isSubmitting}
			maxWidth='sm'
			validationSchema={validationSchema}
			testIds={CREATE_PROJECT_TEST_IDS}
		/>
	)
}
